import { authRefreshResponseSchema } from "@chotca/config";
import { z } from "zod";

const refreshEnvelopeSchema = z.object({
  success: z.literal(true),
  data: authRefreshResponseSchema,
});

export type RotatedTokens = {
  accessToken: string;
  refreshToken: string;
};

export type RefreshExchangeResult =
  | { ok: true; tokens: RotatedTokens }
  | { ok: false; reason: "invalid_session" | "unavailable" };

export function isAccessTokenExpired(
  accessToken: string | undefined,
  skewSeconds = 60,
): boolean {
  if (!accessToken) return true;
  const payload = decodeJwtPayload(accessToken);
  const exp = payload?.exp;
  if (typeof exp !== "number") return true;
  return Date.now() / 1000 >= exp - skewSeconds;
}

function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payloadPart = parts[1];
    if (!payloadPart) return null;
    let base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4;
    if (pad) base64 += "=".repeat(4 - pad);
    return JSON.parse(atob(base64)) as { exp?: number };
  } catch {
    return null;
  }
}

async function performRefresh(
  refreshToken: string,
  apiBaseUrl: string,
  init?: RequestInit,
): Promise<RefreshExchangeResult> {
  const base = apiBaseUrl.replace(/\/$/, "");
  let res: Response;
  try {
    const { headers: extraHeaders, ...restInit } = init ?? {};
    res = await fetch(`${base}/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...((extraHeaders as Record<string, string> | undefined) ?? {}),
      },
      body: JSON.stringify({ refreshToken }),
      cache: "no-store",
      ...restInit,
    });
  } catch {
    return { ok: false, reason: "unavailable" };
  }
  if (res.status === 401) {
    return { ok: false, reason: "invalid_session" };
  }
  if (!res.ok) {
    return { ok: false, reason: "unavailable" };
  }
  let json: unknown;
  try {
    json = await res.json();
  } catch {
    return { ok: false, reason: "unavailable" };
  }
  const parsed = refreshEnvelopeSchema.safeParse(json);
  if (!parsed.success) {
    return { ok: false, reason: "unavailable" };
  }
  return { ok: true, tokens: parsed.data.data };
}

type RefreshCacheEntry = {
  promise: Promise<RefreshExchangeResult>;
  result?: RefreshExchangeResult;
};
const refreshInFlight = new Map<string, RefreshCacheEntry>();
const REFRESH_CACHE_MS = 5_000;

export async function exchangeRefreshToken(
  refreshToken: string,
  apiBaseUrl: string,
  init?: RequestInit,
): Promise<RefreshExchangeResult> {
  const cached = refreshInFlight.get(refreshToken);
  if (cached) return cached.promise;

  const entry: RefreshCacheEntry = {
    promise: performRefresh(refreshToken, apiBaseUrl, init).then((result) => {
      entry.result = result;
      setTimeout(() => {
        if (refreshInFlight.get(refreshToken) === entry) {
          refreshInFlight.delete(refreshToken);
        }
      }, REFRESH_CACHE_MS);
      return result;
    }),
  };
  refreshInFlight.set(refreshToken, entry);
  return entry.promise;
}

export function mergeCookieHeader(
  cookieHeader: string | null,
  updates: Record<string, string>,
): string {
  const map = new Map<string, string>();
  const raw = cookieHeader ?? "";
  for (const part of raw.split(";")) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const i = trimmed.indexOf("=");
    if (i === -1) continue;
    const name = trimmed.slice(0, i).trim();
    map.set(name, trimmed.slice(i + 1).trim());
  }
  for (const [k, v] of Object.entries(updates)) {
    map.set(k, v);
  }
  return [...map.entries()].map(([k, v]) => `${k}=${v}`).join("; ");
}
